import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { HomeCard, type HomeCardData } from '../../components/HomeCard';

interface UserPage {
  id: string;
  name: string;
  source_template_id: string | null;
}

// Feste Template-UUIDs aus engine/apps/portal-backend/src/db/seed-page-templates.ts.
// Wenn die da geaendert werden, hier mitziehen.
const TEMPLATE_ID_HEUTE = '00000000-1111-1111-1111-000000000002';

interface TodayResponse {
  cards: HomeCardData[];
}

/**
 * Heute-Tab. Nach dem Pivot 2026-05-27 ist „Heute" eine persoenliche Seite —
 * der hardcoded URL `/uebersicht/heute` redirected den User zu seiner Heute-Page
 * (per `source_template_id` aufgeloest). Wenn der Erst-Login-Hook sie noch
 * nicht angelegt hat, rendert die Seite weiter den klassischen 6-Karten-Grid
 * als Fallback — kein Loop.
 */
export default function UebersichtHeute() {
  const [heutePageId, setHeutePageId] = useState<string | null | undefined>(undefined); // undefined = laed, null = nicht gefunden
  const [fallbackData, setFallbackData] = useState<TodayResponse | null>(null);
  const [fallbackError, setFallbackError] = useState<string | null>(null);

  // Versuch 1: gibt es eine User-Page mit source_template_id = HEUTE?
  useEffect(() => {
    let aborted = false;
    api
      .get<{ success: boolean; data: UserPage[] }>('/portal/my-pages')
      .then((res) => {
        if (aborted) return;
        if (!res.success) { setHeutePageId(null); return; }
        const heute = res.data.find((p) => p.source_template_id === TEMPLATE_ID_HEUTE);
        setHeutePageId(heute ? heute.id : null);
      })
      .catch(() => { if (!aborted) setHeutePageId(null); });
    return () => { aborted = true; };
  }, []);

  // Fallback-Pfad: User hat keine Heute-Page → klassische Sicht laden
  useEffect(() => {
    if (heutePageId !== null) return; // nur wenn null (= nicht gefunden)
    let aborted = false;
    api
      .get<{ success: boolean; data: TodayResponse }>('/portal/home/today')
      .then((res) => {
        if (aborted) return;
        if (res.success) setFallbackData(res.data);
        else setFallbackError('Dashboard konnte nicht geladen werden');
      })
      .catch(() => { if (!aborted) setFallbackError('Fehler beim Laden'); });
    return () => { aborted = true; };
  }, [heutePageId]);

  // 1. Loading
  if (heutePageId === undefined) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // 2. Persoenliche Seite gefunden → dorthin redirecten
  if (heutePageId) {
    return <Navigate to={`/uebersicht/page/${heutePageId}`} replace />;
  }

  // 3. Kein Page-Eintrag (z.B. Erst-Login noch nicht durch) → Fallback-Rendering
  //    der klassischen Heute-Sicht. Damit hat der User wenigstens Daten waehrend
  //    der Auto-Seed laeuft. Beim naechsten Aufruf greift der Redirect.
  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <h1 className="text-2xl font-semibold mb-1">Heute</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Dein persönliches Dashboard mit den wichtigsten Karten für den Tag.
      </p>

      {fallbackError ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
          {fallbackError}
        </div>
      ) : !fallbackData ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : fallbackData.cards.length === 0 ? (
        <div className="rounded-lg border border-dashed border-default p-12 text-center text-sm text-muted-foreground">
          Keine Karten für deine Rolle konfiguriert.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fallbackData.cards.map((card) => (
            <HomeCard key={card.id} card={card} />
          ))}
        </div>
      )}
    </div>
  );
}
